import React, { Component } from "react";
import Axios from "axios";
import { getSettings } from "../../services/settings/actions";
import { connect } from "react-redux";
import {
        getSingleLanguageData,
        getAllLanguages,
} from "../../services/languages/actions";

const UPDATE_ATTEMPT_KEY = "checkVersionUpdateAttempt";
const REQUEST_TIMEOUT_MS = 8000;
const DATA_REFRESH_TIMEOUT_MS = 6000;
const RELOAD_DELAY_MS = 1500;

class CheckVersion extends Component {
        state = {
                updating: false,
        };

        reloadTimer = null;
        isComponentMounted = false;

        componentDidMount() {
                this.isComponentMounted = true;
                this.checkVersion();
        }

        componentWillUnmount() {
                this.isComponentMounted = false;

                if (this.reloadTimer !== null) {
                        clearTimeout(this.reloadTimer);
                }
        }

        isValidVersion = (value) => {
                return (
                        (typeof value === "string" || typeof value === "number") &&
                        String(value).trim() !== "" &&
                        String(value) !== "undefined" &&
                        String(value) !== "null"
                );
        };

        waitFor = (promise, timeout) => {
                return Promise.race([
                        Promise.resolve(promise).catch(() => null),
                        new Promise((resolve) => {
                                setTimeout(() => resolve(null), timeout);
                        }),
                ]);
        };

        restoreLocalStorage = (backup) => {
                Object.keys(backup).forEach((key) => {
                        if (backup[key] !== null) {
                                localStorage.setItem(key, backup[key]);
                        }
                });
        };

        clearLocalStorage = (
                forceNewSettingsServerVersion,
                forceCacheClearServerVersion
        ) => {
                const backup = {
                        geoLocation: localStorage.getItem("geoLocation"),
                        userSetAddress: localStorage.getItem("userSetAddress"),
                        storeColor: localStorage.getItem("storeColor"),
                        storeLogo: localStorage.getItem("storeLogo"),
                        updatingMessage: localStorage.getItem("updatingMessage"),
                        userPreferedLanguage: localStorage.getItem(
                                "userPreferedLanguage"
                        ),
                        activeRestaurant:
                                localStorage.getItem("activeRestaurant") !== null
                                        ? localStorage.getItem("activeRestaurant")
                                        : "1",
                };

                localStorage.clear();
                this.restoreLocalStorage(backup);

                // Guardar las versiones antes de tareas de red para impedir ciclos.
                localStorage.setItem(
                        "forceNewSettingsVersion",
                        forceNewSettingsServerVersion
                );
                localStorage.setItem(
                        "forceCacheClearVersion",
                        forceCacheClearServerVersion
                );
        };

        clearBrowserCache = async () => {
                const tasks = [];

                if (
                        typeof navigator !== "undefined" &&
                        navigator.serviceWorker &&
                        typeof navigator.serviceWorker.getRegistrations === "function"
                ) {
                        tasks.push(
                                navigator.serviceWorker
                                        .getRegistrations()
                                        .then((registrations) =>
                                                Promise.all(
                                                        registrations.map((registration) =>
                                                                registration.unregister()
                                                        )
                                                )
                                        )
                        );
                }

                if (
                        typeof caches !== "undefined" &&
                        typeof caches.keys === "function"
                ) {
                        tasks.push(
                                caches
                                        .keys()
                                        .then((names) =>
                                                Promise.all(
                                                        names.map((name) => caches.delete(name))
                                                )
                                        )
                        );
                }

                await Promise.all(
                        tasks.map((task) => Promise.resolve(task).catch(() => null))
                );
        };

        refreshSettingsAndLanguage = async () => {
                await this.waitFor(
                        this.props.getSettings(),
                        DATA_REFRESH_TIMEOUT_MS
                );

                const preferredLanguage = localStorage.getItem(
                        "userPreferedLanguage"
                );

                if (preferredLanguage !== null) {
                        await this.waitFor(
                                this.props.getSingleLanguageData(preferredLanguage),
                                DATA_REFRESH_TIMEOUT_MS
                        );
                        return;
                }

                const languages = await this.waitFor(
                        this.props.getAllLanguages(),
                        DATA_REFRESH_TIMEOUT_MS
                );

                const languageList =
                        languages && Array.isArray(languages.payload)
                                ? languages.payload
                                : [];

                const defaultLanguage = languageList.find(
                        (language) => language.is_default === 1
                );

                if (defaultLanguage) {
                        await this.waitFor(
                                this.props.getSingleLanguageData(defaultLanguage.id),
                                DATA_REFRESH_TIMEOUT_MS
                        );
                }
        };

        reloadOnce = () => {
                this.reloadTimer = setTimeout(() => {
                        if (this.isComponentMounted) {
                                this.setState({ updating: false }, () => {
                                        window.location.reload();
                                });
                        } else {
                                window.location.reload();
                        }
                }, RELOAD_DELAY_MS);
        };

        processCacheUpdate = async (
                forceNewSettingsServerVersion,
                forceCacheClearServerVersion
        ) => {
                const attemptedVersion = sessionStorage.getItem(
                        UPDATE_ATTEMPT_KEY
                );

                // Si este WebView ya intentó esta versión, se recupera sin recargar.
                if (attemptedVersion === forceCacheClearServerVersion) {
                        localStorage.setItem(
                                "forceNewSettingsVersion",
                                forceNewSettingsServerVersion
                        );
                        localStorage.setItem(
                                "forceCacheClearVersion",
                                forceCacheClearServerVersion
                        );

                        if (this.isComponentMounted) {
                                this.setState({ updating: false });
                        }

                        return;
                }

                sessionStorage.setItem(
                        UPDATE_ATTEMPT_KEY,
                        forceCacheClearServerVersion
                );

                if (this.isComponentMounted) {
                        this.setState({ updating: true });
                }

                this.clearLocalStorage(
                        forceNewSettingsServerVersion,
                        forceCacheClearServerVersion
                );

                try {
                        await this.clearBrowserCache();
                        await this.refreshSettingsAndLanguage();
                } catch (error) {
                        console.warn("CheckVersion update recovery:", error);
                }

                this.reloadOnce();
        };

        checkVersion = async () => {
                try {
                        const response = await Axios.get(
                                `/version.json?nocache=${Date.now()}`,
                                {
                                        timeout: REQUEST_TIMEOUT_MS,
                                        headers: {
                                                "Cache-Control": "no-cache",
                                                Pragma: "no-cache",
                                        },
                                }
                        );

                        const data = response && response.data;

                        // Rechazar HTML, respuestas vacías o JSON incompleto.
                        if (
                                !data ||
                                typeof data !== "object" ||
                                Array.isArray(data) ||
                                !this.isValidVersion(
                                        data.forceNewSettingsVersion
                                ) ||
                                !this.isValidVersion(
                                        data.forceCacheClearVersion
                                )
                        ) {
                                console.warn(
                                        "CheckVersion ignored an invalid version.json response"
                                );
                                return;
                        }

                        const forceNewSettingsServerVersion = String(
                                data.forceNewSettingsVersion
                        );
                        const forceCacheClearServerVersion = String(
                                data.forceCacheClearVersion
                        );

                        const clientSettingsVersion = localStorage.getItem(
                                "forceNewSettingsVersion"
                        );
                        const clientCacheVersion = localStorage.getItem(
                                "forceCacheClearVersion"
                        );

                        if (
                                clientSettingsVersion === null &&
                                clientCacheVersion === null
                        ) {
                                localStorage.setItem(
                                        "forceNewSettingsVersion",
                                        forceNewSettingsServerVersion
                                );
                                localStorage.setItem(
                                        "forceCacheClearVersion",
                                        forceCacheClearServerVersion
                                );
                                return;
                        }

                        if (
                                clientSettingsVersion !==
                                forceNewSettingsServerVersion
                        ) {
                                localStorage.setItem(
                                        "forceNewSettingsVersion",
                                        forceNewSettingsServerVersion
                                );

                                this.waitFor(
                                        this.props.getSettings(),
                                        DATA_REFRESH_TIMEOUT_MS
                                );
                        }

                        if (
                                clientCacheVersion !==
                                forceCacheClearServerVersion
                        ) {
                                await this.processCacheUpdate(
                                        forceNewSettingsServerVersion,
                                        forceCacheClearServerVersion
                                );
                        }
                } catch (error) {
                        // Una falla de red o timeout nunca debe bloquear la aplicación.
                        console.warn("CheckVersion request failed:", error.message);

                        if (this.isComponentMounted) {
                                this.setState({ updating: false });
                        }
                }
        };

        render() {
                const { updating } = this.state;

                return (
                        <React.Fragment>
                                {updating && (
                                        <React.Fragment>
                                                <div className="update-full-notification">
                                                        <span className="spin-load" />
                                                </div>

                                                <div
                                                        className="update-full-notification"
                                                        style={{ zIndex: 9999999999 }}
                                                >
                                                        <h1
                                                                className="d-flex"
                                                                style={{ marginTop: "8rem" }}
                                                        >
                                                                {localStorage.getItem(
                                                                        "updatingMessage"
                                                                ) ||
                                                                        "Actualizando Manjar2Go..."}
                                                        </h1>
                                                </div>
                                        </React.Fragment>
                                )}
                        </React.Fragment>
                );
        }
}

const mapStateToProps = (state) => ({
        settings: state.settings.settings,
});

export default connect(
        mapStateToProps,
        {
                getSettings,
                getSingleLanguageData,
                getAllLanguages,
        }
)(CheckVersion);